// src/pages/admin/UserManagementPage.js
// src/pages/admin/UserManagementPage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Modal, TextField,
    Select, MenuItem, InputLabel, FormControl,
    FormGroup, FormControlLabel, Checkbox, CircularProgress, Alert, Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';



// Estilo para los Modales
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    
    // Unificamos el estado del modal y del formulario
    const [openFormModal, setOpenFormModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingUser, setEditingUser] = useState(null);
    const [formError, setFormError] = useState('');

    // Estados para el modal de asignación de tiendas
    const [openAssignModal, setOpenAssignModal] = useState(false);
    const [allStores, setAllStores] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignedStores, setAssignedStores] = useState({});
    const [loadingStores, setLoadingStores] = useState(false);

    
    useEffect(() => {
        fetchUsers();
        fetchAllStores(); // Cargar la lista de tiendas una vez
    }, []);




    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error("Error al obtener los usuarios:", error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchAllStores = async () => {
        setLoadingStores(true);
        try {
            const response = await api.get('/stores');
            setAllStores(response.data);
        } catch (error) {
            console.error("Error al obtener las tiendas:", error);
        } finally {
            setLoadingStores(false);
        }
    };

    // --- Lógica para el modal de CREACIÓN de usuario ---
     const handleOpenCreateModal = () => {
        setEditingUser(null);
        setFormData({
            fullname: '', email: '', password: '', role_name: 'Cajero', is_active: true
        });
        setFormError('');
        setOpenFormModal(true);
    };

 const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setFormData({
            fullname: user.fullname,
            email: user.email,
            is_active: user.is_active,
            // La contraseña y el rol no se editan aquí por simplicidad
        });
        setFormError('');
        setOpenFormModal(true);
    };

    const handleCloseFormModal = () => setOpenFormModal(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFormData({ ...formData, [name]: val });
    };

    const handleSubmit = async () => {
        setFormError('');
        try {
            if (editingUser) {
                // El backend espera 'is_active', no podemos enviar campos extra como 'password' o 'role_name'
                const payload = {
                    fullname: formData.fullname,
                    email: formData.email,
                    is_active: !!formData.is_active // Aseguramos que sea booleano
                };
                await api.put(`/users/${editingUser.id}`, payload);
            } else {
                await api.post('/users', formData);
            }
            handleCloseFormModal();
            await fetchUsers();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Error al guardar el usuario.');
        }
    };
    

    // --- Lógica para el modal de ASIGNACIÓN de tiendas ---
    const handleOpenAssignModal = (user) => {
        setSelectedUser(user);
        // Pre-llenar los checkboxes con las tiendas ya asignadas al usuario
        const currentlyAssigned = user.assigned_stores ? user.assigned_stores.split(', ').map(name => {
            const store = allStores.find(s => s.name === name);
            return store ? store.id : null;
        }).filter(Boolean) : [];
        
        const initialAssigned = allStores.reduce((acc, store) => {
            acc[store.id] = currentlyAssigned.includes(store.id);
            return acc;
        }, {});

        setAssignedStores(initialAssigned);
        setOpenAssignModal(true);
    };
    const handleCloseAssignModal = () => setOpenAssignModal(false);
    const handleCheckboxChange = (event) => {
        setAssignedStores({
            ...assignedStores,
            [event.target.name]: event.target.checked,
        });
    };
    const handleSaveAssignments = async () => {
        const storeIds = Object.keys(assignedStores).filter(id => assignedStores[id]);
        try {
            await api.post(`/users/${selectedUser.id}/assign-stores`, { storeIds });
            handleCloseAssignModal();
            fetchUsers(); // Recargar usuarios para ver los cambios
        } catch (error) {
            console.error("Error al asignar tiendas:", error);
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'fullname', headerName: 'Nombre Completo', width: 220 },
        { field: 'email', headerName: 'Correo', width: 220 },
        { field: 'role_name', headerName: 'Rol', width: 130 },
        { field: 'assigned_stores', headerName: 'Tiendas Asignadas', width: 200, sortable: false },
        { field: 'is_active', headerName: 'Activo', width: 100, type: 'boolean' },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 250,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <Button
                        variant="outlined" size="small" sx={{ mr: 1 }}
                        onClick={() => handleOpenEditModal(params.row)}
                    >
                        Editar
                    </Button>
                    <Button
                        variant="outlined" size="small"
                        onClick={() => handleOpenAssignModal(params.row)}
                    >
                        Asignar Tiendas
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Usuarios</Typography>
                <Button variant="contained" onClick={handleOpenCreateModal}>Añadir Nuevo Usuario</Button>
            </Box>
            <DataGrid rows={users} columns={columns} loading={loadingUsers} />

            {/* Modal de CREACIÓN / EDICIÓN */}
            <Modal open={openFormModal} onClose={handleCloseFormModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</Typography>
                    {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
                    
                    <TextField margin="normal" required fullWidth label="Nombre Completo" name="fullname" value={formData.fullname || ''} onChange={handleInputChange} />
                    <TextField margin="normal" required fullWidth label="Correo" name="email" type="email" value={formData.email || ''} onChange={handleInputChange} />
                    
                    {!editingUser && ( // Solo mostrar campos de contraseña y rol al crear
                        <>
                            <TextField margin="normal" required fullWidth label="Contraseña" name="password" type="password" value={formData.password || ''} onChange={handleInputChange} />
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Rol</InputLabel>
                                <Select name="role_name" value={formData.role_name || 'Cajero'} label="Rol" onChange={handleInputChange}>
                                    <MenuItem value={'Cajero'}>Cajero</MenuItem>
                                    <MenuItem value={'Gerente'}>Gerente</MenuItem>
                                    <MenuItem value={'Administrador'}>Administrador</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {editingUser && ( // Solo mostrar el switch de activar/desactivar al editar
                        <FormControlLabel
                            control={<Switch checked={!!formData.is_active} onChange={handleInputChange} name="is_active" />}
                            label="Usuario Activo"
                        />
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseFormModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>{editingUser ? 'Guardar Cambios' : 'Crear'}</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal de ASIGNACIÓN de Tiendas */}
            <Modal open={openAssignModal} onClose={handleCloseAssignModal}>
                <Box sx={style}>
                    <Typography variant="h6" component="h2">
                        Asignar tiendas a {selectedUser?.fullname}
                    </Typography>
                    {loadingStores ? <CircularProgress /> : (
                        <FormGroup sx={{ mt: 2 }}>
                            {allStores.map(store => (
                                <FormControlLabel
                                    key={store.id}
                                    control={
                                        <Checkbox
                                            checked={!!assignedStores[store.id]}
                                            onChange={handleCheckboxChange}
                                            name={String(store.id)}
                                        />
                                    }
                                    label={store.name}
                                />
                            ))}
                        </FormGroup>
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseAssignModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSaveAssignments}>Guardar Cambios</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default UserManagementPage;